// ============================================================
// notifications.types.ts
// User-level email-notification preferences — booleans on the User
// row's notificationPreferences Json column. Travel-on-AuthUser via
// GET / PATCH /api/auth/me, mirroring the viewingPaneToggles precedent.
//
// These now drive REAL sends: the assign/complete event-notifies
// (lib/notifications.ts) and the Stage-4 scan alerts — day-reminder,
// deadline-approaching, task-due (reminderScan / deadlineTaskScan,
// gated via lib/scanEmail.notificationEmailAllowed). The bell always
// fires; these prefs gate the companion EMAIL only.
//
// Defaults are merged over whatever's on authUser at the Settings
// panel's render time, so missing keys (brand-new accounts, or future
// prefs added before the user has visited the panel) get sensible
// fallbacks. The backend gate applies the SAME defaults independently
// (opt-out keys send unless === false; opt-in keys send only === true),
// so a user who's never opened Settings still gets the right behaviour.
// ============================================================

export interface NotificationPreferences {
  // Email the user when a task is assigned to them (by
  // themselves or by another firm member).
  emailOnTaskAssigned: boolean
  // Email the user when a task they CREATED is completed by
  // someone else. Also gated firm-side by the firm's
  // taskCompleteNotify routing (creator / creator+admins /
  // admins) — this pref only opts an eligible recipient OUT.
  // Opt-out (default on), mirroring task assignment.
  emailOnTaskCompleted: boolean
  // Email the user when a CASE is assigned to them by another
  // firm member. Opt-out (default on), mirroring task assignment;
  // self-assignment and unassign never email (enforced backend-side).
  emailOnCaseAssigned: boolean
  // Email the case assignee when one of their case deadlines is
  // approaching (a few days out) or due today. Opt-out (default on)
  // — deadlines are don't-miss. Driven by deadlineTaskScan.
  emailBeforeDeadline: boolean
  // Email the user when a personal calendar reminder they set falls
  // due. Opt-out (default on) — the user asked for the reminder, so
  // they want it. Driven by reminderScan.
  emailOnDayReminder: boolean
  // Email the task assignee when a task assigned to them is due today.
  // Opt-IN (default OFF) — potentially high-volume, so it doesn't spam
  // the inbox unless the user turns it on. Driven by deadlineTaskScan.
  emailOnTaskDue: boolean
  // Email about bill approvals: an admin when a bill is sent for their
  // approval; the person who sent it when it's approved or sent back.
  // Opt-out (default on) — approvals hold up issuing. Driven by
  // lib/billApproval (the bell always fires).
  emailOnBillApproval: boolean
  // ── In-app (not email, not a browser / OS notification) ──
  // A brief toast in the app when a new bell notification arrives. Default ON.
  popupOnNotification: boolean
  // A short chime in the app when a new bell notification arrives. Default
  // OFF (a sound is intrusive unless asked for). Each is independent.
  soundOnNotification: boolean
  // Daily morning digest summarising open tasks + upcoming
  // deadlines. Opt-in by default (off) to avoid inbox fatigue.
  emailDailyDigest: boolean
  // Round 2 — personal EXTRA deadline warnings, per deadline type:
  // { [deadlineTypeId]: days before[] }. They only ADD to the firm's schedule
  // (Admin › Deadline types) — the firm's warnings can never be removed here.
  // The backend sanitises the map (whole days 1–365, de-duplicated).
  deadlineExtraDays?: Record<string, number[]>
}

// The on/off switches (every preference except the extra-days map).
export type BooleanNotificationPreference = Exclude<keyof NotificationPreferences, 'deadlineExtraDays'>

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  emailOnTaskAssigned: true,
  emailOnTaskCompleted: true,
  emailOnCaseAssigned: true,
  emailBeforeDeadline: true,
  emailOnDayReminder: true,
  emailOnTaskDue: false,
  emailOnBillApproval: true,
  popupOnNotification: true,
  soundOnNotification: false,
  emailDailyDigest: false,
}
